import { FieldPath, useForm } from "react-hook-form"
import { Checkbox, FormControl, FormControlLabel, FormGroup } from "@mui/material"
import { TextFieldElement } from "react-hook-form-mui";
import { LocalFormSchema } from "../logic/applicationUpdateSchema";
import { asOptions, countryCodeMapping } from "../utils/option-data";
import EnumBackedAutocomplete from "./EnumBackedAutocomplete";
import OptionalTextField from "./OptionalTextField";
import StringFormattedControlledDatePicker from "./StringFormattedControlledDatePicker";

export const noFixedAbodeAddressTemplate = {
  addressLine1: 'No Fixed Abode',
  addressLine2: '',
  addressTown: 'Unknown',
  addressCounty: '',
  postcode: '',
  countryCode: '',
}

type AddressBlockProps = {
  form: ReturnType<typeof useForm<LocalFormSchema>>;
  allowNoFixedAbode?: boolean;
  addressLine1: FieldPath<LocalFormSchema>;
  addressLine2: FieldPath<LocalFormSchema>;
  addressTown: FieldPath<LocalFormSchema>;
  addressCounty: FieldPath<LocalFormSchema>;
  postcode: FieldPath<LocalFormSchema>;
  countryCode: FieldPath<LocalFormSchema>;
  residentFromGyearMonth: FieldPath<LocalFormSchema>;
  residentToGyearMonth?: FieldPath<LocalFormSchema>;
  disabled?: boolean;
}
const AddressBlock: React.FC<AddressBlockProps> = ({ form, allowNoFixedAbode = true, addressLine1, addressLine2, addressTown, addressCounty, postcode, countryCode, residentFromGyearMonth, residentToGyearMonth, disabled = false }) => {
  const textFieldElementCommonProps = {
    control: form.control,
    margin: "normal" as const,
    fullWidth: true,
  }

  const isNoFixedAbode = form.watch(addressLine1) === 'No Fixed Abode'

  const filterOptionsNoGB = (option: { id: string, label: string }) => option.id !== 'GB'
  const filterOptionsAll = () => true

  return <>
    {allowNoFixedAbode && (
      <FormControl>
        <FormGroup row>
          <FormControlLabel
            label="I was travelling abroad and had no fixed abode"
            control={
              <Checkbox
                color="primary"
                value={isNoFixedAbode}
                checked={isNoFixedAbode}
                onChange={(ev) => {
                  if (ev.target.checked) {
                    form.setValue(addressLine1, noFixedAbodeAddressTemplate.addressLine1)
                    form.setValue(addressLine2, noFixedAbodeAddressTemplate.addressLine2)
                    form.setValue(addressTown, noFixedAbodeAddressTemplate.addressTown)
                    form.setValue(addressCounty, noFixedAbodeAddressTemplate.addressCounty)
                    form.setValue(postcode, noFixedAbodeAddressTemplate.postcode)
                    form.setValue(countryCode, noFixedAbodeAddressTemplate.countryCode)
                  } else {
                    form.setValue(addressLine1, '')
                    form.setValue(addressLine2, '')
                    form.setValue(addressTown, '')
                    form.setValue(addressCounty, '')
                    form.setValue(postcode, '')
                    form.setValue(countryCode, '')
                  }
                }}
                disabled={disabled}
              />
            }
          />
        </FormGroup>
      </FormControl>
    )}

    <TextFieldElement {...textFieldElementCommonProps} label="Address Line 1" name={addressLine1} disabled={isNoFixedAbode || disabled} />
    <OptionalTextField {...textFieldElementCommonProps} label="Address Line 2" name={addressLine2} disabled={isNoFixedAbode || disabled} />
    <TextFieldElement {...textFieldElementCommonProps} label="Town" name={addressTown} disabled={isNoFixedAbode || disabled} />
    <OptionalTextField {...textFieldElementCommonProps} label="County" name={addressCounty} disabled={isNoFixedAbode || disabled} />
    <TextFieldElement {...textFieldElementCommonProps} label="Postcode" name={postcode} disabled={isNoFixedAbode || disabled} />
    {/* See: https://stackoverflow.com/questions/7357256/disabled-form-inputs-do-not-appear-in-the-request */}
    {isNoFixedAbode && <>
      <input type="hidden" name={addressLine1} value={form.getValues(addressLine1) as string | undefined} />
      <input type="hidden" name={addressLine2} value={form.getValues(addressLine2) as string | undefined} />
      <input type="hidden" name={addressTown} value={form.getValues(addressTown) as string | undefined} />
      <input type="hidden" name={addressCounty} value={form.getValues(addressCounty) as string | undefined} />
      <input type="hidden" name={postcode} value={form.getValues(postcode) as string | undefined} />
    </>}

    <EnumBackedAutocomplete
      control={form.control}
      sx={{ marginTop: 2 }}
      label="Country"
      name={countryCode}
      options={asOptions(countryCodeMapping).filter(isNoFixedAbode ? filterOptionsNoGB : filterOptionsAll )}
      disabled={disabled}
    />
    {residentToGyearMonth && (
      <StringFormattedControlledDatePicker
        control={form.control}
        sx={{ marginTop: 4 }}
        label="Resident To"
        name={residentToGyearMonth}
        views={['month', 'year']}
        format="yyyy-MM"
        disabled={disabled}
      />
    )}
    <StringFormattedControlledDatePicker
      control={form.control}
      sx={{ marginTop: 4 }}
      label="Resident From"
      name={residentFromGyearMonth}
      views={['month', 'year']}
      format="yyyy-MM"
      disabled={disabled}
    />
  </>
}

export default AddressBlock
